import React, { Component } from "react";
import Header from "../../components/header/Header";
import { handleLandingScroll } from "../../utils/scroll";
import { trackPageview, track } from "../../utils/analytics";

function Role({ when, where, title, italic, stack, children }) {
  return (
    <div className="role-block">
      <div className="when"><b>{when}</b>{where ? <span className="where">{where}</span> : null}</div>
      <div>
        <h3>{title} {italic ? <em>{italic}</em> : null}</h3>
        {stack ? <div className="where-line">{stack}</div> : null}
        <p>{children}</p>
      </div>
      <div className="side"></div>
    </div>
  );
}

function CV_PDF_Download() {
  return (
    <a className="btn-pill" href="/resume.pdf" download="walid-maaninou-cv.pdf">
      ↓ DOWNLOAD ORIGINAL (FR)
    </a>
  );
}

class CV extends Component {
  componentDidMount() {
    handleLandingScroll(this.props.location);
    trackPageview();
  }

  handlePrint = () => {
    track("cv_export_pdf");
    window.print();
  };

  render() {
    return (
      <>
        <div className="grid-bg print-hide" />
        <div className="print-hide"><Header theme={this.props.theme} /></div>

        <div className="wrap cv-wrap">

          {/* HERO */}
          <section className="hero">
            <div className="label-strip">
              <span><b>CURRICULUM VITAE</b></span>
              <span>TECH LEAD · CASABLANCA</span>
              <span><b>6+</b> YEARS</span>
            </div>

            <div className="cv-hero-grid">
              <h1 className="huge">
                <span className="row">MAANINOU</span>
                <span className="row">WALID<span className="accent-bg">.</span></span>
                <span className="row indent"><span className="ital">tech</span></span>
                <span className="row indent"><span className="strike">lead.</span></span>
              </h1>
              <div className="portrait-frame">
                <img
                  className="portrait"
                  src="/images/personal-img.png"
                  alt="Walid Maaninou"
                />
                <span className="portrait-caption">TECH LEAD</span>
              </div>
            </div>

            <div className="sub-row">
              <p>
                <b>Tech Lead</b> with 6+ years of Python and cloud-native
                architecture experience. Hands-on on the design of scalable
                systems, CI/CD, and AWS. I lead teams, drive architecture
                decisions, and mentor developers day-to-day.
              </p>
              <div className="meta-block">
                <div className="k">PROFILE</div>
                <div className="row"><span className="lab">NAME</span><span>WALID MAANINOU</span></div>
                <div className="row"><span className="lab">AGE</span><span>30</span></div>
                <div className="row"><span className="lab">STATUS</span><span>MARRIED</span></div>
                <div className="row"><span className="lab">NATIONALITY</span><span>MOROCCAN</span></div>
                <div className="row"><span className="lab">LANGUAGES</span><span>AR · FR · EN</span></div>
                <div className="row"><span className="lab">CITY</span><span>CASABLANCA</span></div>
              </div>
            </div>

            <div className="cta-row print-hide">
              <button type="button" className="primary cv-print-btn" onClick={this.handlePrint}>
                EXPORT AS PDF <span className="arrow">↓</span>
              </button>
              <a href="/resume.pdf" download="walid-maaninou-cv.pdf" onClick={() => track("cv_download_pdf", { lang: "fr" })}>
                DOWNLOAD ORIGINAL (FR) <span className="arrow">↗</span>
              </a>
              <a href="mailto:maaninou.walid@gmail.com" onClick={() => track("cta_email", { location: "cv_hero" })}>
                EMAIL ME <span className="arrow">→</span>
              </a>
            </div>
          </section>

          {/* EXPERIENCE */}
          <section className="section" id="experience">
            <div className="section-head">
              <h2 className="section-title">01 <em>·</em> EXPERIENCE</h2>
              <div className="meta"><b>03</b><br/>ROLES</div>
            </div>

            <div className="role-list">
              <Role
                when="APR 2025 — PRESENT"
                where="AFD TECHNOLOGIES · ACCENTURE"
                title="TECH"
                italic="lead"
                stack="PYTHON · DJANGO · AWS · CI/CD"
              >
                Lead a team of automation engineers — owning technical and
                architecture decisions, code reviews, mentoring juniors, and
                continuously improving monitoring and troubleshooting of
                production workflows. Hands-on on critical work.
              </Role>

              <Role
                when="FEB 2023 — APR 2025"
                where="AFD TECHNOLOGIES · ACCENTURE"
                title="SENIOR"
                italic="python &amp; automation engineer"
                stack="PYTHON · FASTAPI · STREAMLIT · SQL"
              >
                Designed and built automation solutions for data processing
                and web tasks. Built Python applications (Streamlit / Django)
                that let telecoms engineers simplify and automate their
                day-to-day workflows.
              </Role>

              <Role
                when="OCT 2020 — FEB 2023"
                where="UPWORK · REMOTE"
                title="FULL-STACK &amp;"
                italic="automation engineer"
                stack="PYTHON · CI/CD · AWS"
              >
                Python freelancer: designing automation solutions and
                Django / Flask back-ends, developing React applications, and
                setting up data pipelines and scraping systems deployed via
                CI/CD workflows in cloud environments.
              </Role>
            </div>
          </section>

          {/* EDUCATION */}
          <section className="section" id="cv-education">
            <div className="section-head">
              <h2 className="section-title">02 <em>·</em> EDUCATION</h2>
              <div className="meta"><b>03</b><br/>DEGREES</div>
            </div>

            <div className="role-list">
              <Role
                when="2019 — 2020"
                where="HASSANIA SCHOOL OF PUBLIC WORKS · EHTP"
                title="ENGINEERING"
                italic="degree, data engineering"
                stack="STATISTICS · BIG DATA · DATABASES · DEEP LEARNING · CLOUD · BLOCKCHAIN"
              >
                Master's-level engineering degree covering the full data
                stack: statistical studies, data analysis and visualization,
                big data, AI, SQL/NoSQL databases, BI — plus deep learning,
                cloud computing, and blockchain development.
              </Role>

              <Role
                when="2016 — 2019"
                where="HASSAN II UNIVERSITY · AÏN CHOCK"
                title="MASTER'S"
                italic="in mathematics"
                stack="ALGEBRAIC TOPOLOGY · GEOMETRY · MATH PHYSICS · ROBOTIC TOPOLOGY"
              >
                Algebraic topology and geometry, with detours into
                mathematical physics and robotic topology. Most of it was the
                kind of mathematics you do because it's beautiful — the
                practical payoff came later.
              </Role>

              <Role
                when="2013 — 2016"
                where="HASSAN II UNIVERSITY · BEN M'SIK"
                title="B.SC."
                italic="applied mathematics"
                stack="ALGEBRA · CALCULUS · GEOMETRY · STATISTICS · PROBABILITY"
              >
                Standard mathematics foundation, taught carefully. Best
                decision in those years was taking the optional algorithms
                electives.
              </Role>
            </div>
          </section>

          {/* CERTIFICATIONS */}
          <section className="section" id="cv-certs">
            <div className="section-head">
              <h2 className="section-title">03 <em>·</em> CERTIFICATIONS</h2>
              <div className="meta"><b>04</b><br/>COMPLETED</div>
            </div>

            <div className="cert-grid">
              <div className="cert-cell">
                <div className="num">CERT 01 · JAN — FEB 2020</div>
                <h4>IBM BIG DATA <em>engineer v2 mastery award</em></h4>
                <div className="by">
                  IBM
                  <br />Apache Hadoop · Ambari · Spark · Big SQL · HDFS · YARN · MapReduce · Zookeeper · Knox · Sqoop · HBase
                </div>
              </div>
              <div className="cert-cell">
                <div className="num">CERT 02 · MAR — AUG 2020</div>
                <h4>DEEP LEARNING <em>specialization</em></h4>
                <div className="by">
                  Andrew Ng · Coursera
                  <br />Neural network · CNN · RNN · LSTM · Adam · Dropout
                </div>
              </div>
              <div className="cert-cell">
                <div className="num">CERT 03 · MAR — AUG 2020</div>
                <h4>TENSORFLOW <em>in practice specialization</em></h4>
                <div className="by">
                  Laurence Moroney · Coursera
                  <br />Computer vision · Tokenization · NLP · Timeseries · Keras
                </div>
              </div>
              <div className="cert-cell">
                <div className="num">CERT 04 · MAR — AUG 2020</div>
                <h4>BLOCKCHAIN <em>specialization</em></h4>
                <div className="by">
                  Bina Ramamurthy · Coursera
                  <br />Solidity · Bitcoin · Ethereum · Dapps · Smart Contracts
                </div>
              </div>
            </div>
          </section>

          {/* PERSONAL PROJECTS */}
          <section className="section" id="cv-projects">
            <div className="section-head">
              <h2 className="section-title">04 <em>·</em> PERSONAL PROJECTS</h2>
              <div className="meta"><b>03</b><br/>SOLO BUILDS</div>
            </div>

            <div className="role-list">
              <Role
                when="FEB — OCT 2022"
                where="SOLO"
                title="PAIRS CRYPTO"
                italic="trading bot"
                stack="DJANGO · REACT · MONGODB · AWS (LAMBDA · DYNAMODB · EC2)"
              >
                Crypto trading bot with a web interface for rule customization.
                Automated data extraction via the Binance API, financial
                metrics computation, and order execution via the Bybit API.
                Architecture with back-testing, monitoring, and automated
                pipelines for reliability and scale.
              </Role>

              <Role
                when="SEP 2021 — JAN 2023"
                where="ALKASHAF.COM"
                title="ARABIC DICTIONARIES"
                italic="SaaS platform"
                stack="FASTAPI · REACT · OPENSEARCH · DYNAMODB · AUTH0 · AWS (APPRUNNER · AMPLIFY)"
              >
                SaaS platform for Arabic dictionaries — solo project,
                end-to-end design and development. FastAPI + React,
                multi-dictionary advanced search via OpenSearch, favorites
                persistence on DynamoDB, Auth0 authentication, AWS
                AppRunner / Amplify deployment with CI/CD.
              </Role>

              <Role
                when="AUG 2021 — DEC 2022"
                where="SOLO · AUTO MARKETPLACES"
                title="AUTO LISTINGS"
                italic="extraction platform"
                stack="REACT · DJANGO · WEB SCRAPING"
              >
                Web platform for orchestrating Facebook and Kijiji scrapers:
                hourly extraction of vehicle listings (model, brand, year,
                price, mileage) across multiple regions. Google Sheets
                integration for real-time tracking; web UI for filter and
                location management.
              </Role>
            </div>
          </section>

          {/* CONTACT BLOCK (printable) */}
          <section className="section" id="cv-contact">
            <div className="section-head">
              <h2 className="section-title">05 <em>·</em> CONTACT</h2>
              <div className="meta">REPLY<br/><b>&lt; 24H</b></div>
            </div>

            <div className="channel-list">
              <div className="channel">
                <span className="ch-label">EMAIL</span>
                <span className="ch-val"><a href="mailto:maaninou.walid@gmail.com">maaninou.walid@gmail.com</a></span>
                <span className="ch-go">→</span>
              </div>
              <div className="channel">
                <span className="ch-label">PHONE</span>
                <span className="ch-val"><a href="tel:+212611115779">+212 611 115779</a></span>
                <span className="ch-go">↗</span>
              </div>
              <div className="channel">
                <span className="ch-label">WEB</span>
                <span className="ch-val"><a href="https://walidmaaninou.com">walidmaaninou.com</a></span>
                <span className="ch-go">↗</span>
              </div>
              <div className="channel">
                <span className="ch-label">LINKEDIN</span>
                <span className="ch-val"><a href="https://www.linkedin.com/in/walid-maaninou-86a844102/">in/walid-maaninou</a></span>
                <span className="ch-go">↗</span>
              </div>
              <div className="channel">
                <span className="ch-label">UPWORK</span>
                <span className="ch-val"><a href="https://upwork.com/freelancers/walidmaaninou">upwork.com/freelancers/walidmaaninou</a></span>
                <span className="ch-go">↗</span>
              </div>
              <div className="channel">
                <span className="ch-label">ADDRESS</span>
                <span className="ch-val">48 rue Koronfol, Hay Mohammadi · Casablanca</span>
                <span className="ch-go"></span>
              </div>
            </div>
          </section>

          <div className="print-only cv-print-footer">
            <hr />
            <p>WALIDMAANINOU.COM · MAANINOU.WALID@GMAIL.COM · +212 611 115779</p>
          </div>
        </div>

        {/* footer hidden on print */}
        <div className="print-hide">
          <footer className="site-footer">
            <span>© 2026 MAANINOU WALID · CASABLANCA · GMT+1</span>
            <span>
              <a href="mailto:maaninou.walid@gmail.com">EMAIL</a>
              <a href="https://github.com/walidMaaninou">GITHUB</a>
              <a href="https://www.linkedin.com/in/walid-maaninou-86a844102/">LINKEDIN</a>
            </span>
          </footer>
        </div>
      </>
    );
  }
}

export default CV;
